import {Button, createStyles, Group, Stack, Tabs} from '@mantine/core';
import React from 'react';

import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import CompatibilityCheckTab from 'analysis-tool/ui/metadata-modal/compatibility/CompatibilityCheckTab';
import VariablesSummaryTab from 'analysis-tool/ui/metadata-modal/compatibility/VariablesSummaryTab';
import getTestProps from 'test/lib/cypress/getTestProps';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';
import type {RunModalProps} from 'ui-library/runModal';

export type AnalysisTemplateCompatibilityModalProps = RunModalProps<void> & {
  analysisTemplate: AnalysisTemplateGraphQL;
};

enum AnalysisTemplateCompatibilityModalTab {
  INPUTS = 'inputs',
  COMPATIBILITY = 'compatibility',
}

const useStyles = createStyles((theme) => ({
  tabLabel: {
    fontSize: theme.fontSizes.lg,
    fontWeight: 600,
  },
}));

/**
 * A modal showing an analysis template's input variables and its compatibility with integrations that
 * support building on top of analysis templates (e.g. lab automation run schemas).
 */
export default function AnalysisTemplateCompatibilityModal({
  analysisTemplate,
  close,
}: AnalysisTemplateCompatibilityModalProps): JSX.Element {
  const {classes} = useStyles();

  return (
    <Stack spacing="md" {...getTestProps({component: 'AnalysisTemplateCompatibilityModal', element: 'root'})}>
      <Tabs
        defaultValue={AnalysisTemplateCompatibilityModalTab.INPUTS}
        classNames={{tabLabel: classes.tabLabel}}
      >
        <Tabs.List>
          <Tabs.Tab
            value={AnalysisTemplateCompatibilityModalTab.INPUTS}
            icon={<BenchlingIcon icon="arrow-right-to-circle" />}
          >
            Input variables
          </Tabs.Tab>
          <Tabs.Tab
            value={AnalysisTemplateCompatibilityModalTab.COMPATIBILITY}
            icon={<BenchlingIcon icon="check-circle-outline" />}
          >
            Compatibility
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value={AnalysisTemplateCompatibilityModalTab.INPUTS} pt="md">
          <VariablesSummaryTab analysisTemplate={analysisTemplate} />
        </Tabs.Panel>
        <Tabs.Panel value={AnalysisTemplateCompatibilityModalTab.COMPATIBILITY} pt="md">
          <CompatibilityCheckTab template={analysisTemplate} />
        </Tabs.Panel>
      </Tabs>
      <ModalFooter>
        <Group position="right">
          <Button
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Group>
      </ModalFooter>
    </Stack>
  );
}
